import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString, normalizeClass as _normalizeClass, createElementVNode as _createElementVNode, createCommentVNode as _createCommentVNode } from "vue"

const _hoisted_1 = { class: "flex-col w-full items-start space-x-1 text-base" }
const _hoisted_2 = { class: "flex" }
const _hoisted_3 = ["data-dusk", "onClick"]
const _hoisted_4 = {
  key: 0,
  class: "flex w-full border-t border-gray-300"
}
const _hoisted_5 = ["data-dusk", "onClick"]

import {
  PropType,
  computed,
  ref
} from 'vue';
import { Status } from '@/models';
import { ConsultStatusSelectorOption } from '@/lib';
import { useI18n } from 'vue-i18n';
import {
  EpisodeOfCareCountKeys,
  EpisodeOfCareStatusCounts
} from '@/services/aggregate/ophthalmology-consult.aggregate.service';

interface ChildTab {
  statuses: EpisodeOfCareCountKeys[];
  label: string;
  name: string;
}
interface TabConsultWithChildren {
  label: string;
  name: string;
  count: number;
  children?: ChildTab[];
}


export default /*#__PURE__*/_defineComponent({
  __name: 'ConsultStatusSelector',
  props: {
  options: {
    type: Array as PropType<Array<ConsultStatusSelectorOption>>,
    required: true
  },
  statusCounts: {
    type: Object as PropType<EpisodeOfCareStatusCounts>,
    required: true
  },
  modelValue: {
    type: Array as PropType<Array<Status | ''>>,
    required: true
  },
  disabled: {
    type: Boolean,
    default: false
  },
  tabs: {
    type: Array as PropType<TabConsultWithChildren[]>,
    default: () => []
  }
},
  emits: ["update:modelValue"],
  setup(__props, { emit }: { emit: ({
    (e: 'update:modelValue', value: Array<Status | ''>): void;
  }), expose: any, slots: any, attrs: any }) {

const props = __props;

const { t } = useI18n();






const optionStyle = computed(() => [
  props.disabled ? 'cursor-not-allowed' : 'cursor-pointer'
]);

const parentOptionStyle = computed(() => [
  optionStyle.value,
  (hasActiveChildren.value && 'pb-4')
]);

const selectedStyle = computed(() => ({
  label: 'text-gray-900',
  total: 'text-white bg-primary-500 rounded'
}));
const unselectedStyle = computed(() => ({
  label: 'text-gray-500',
  total: 'text-gray-500 bg-gray-200 rounded-md'
}));

const totalCounts = computed(() => props.statusCounts);

const getCountPerState = (statuses: EpisodeOfCareCountKeys[]): number =>
  statuses.reduce(
    (total: number, status: EpisodeOfCareCountKeys) =>
      total + totalCounts.value[status],
    0
  );

const tabs = computed(
  () =>
    [
      {
        label: t('custom.uhb.consult.tab-group-view-all'),
        name: 'view-all',
        count: getCountPerState(['All'])
      },
      {
        label: t('custom.uhb.consult.tab-group-consult'),
        name: 'consult',
        count: getCountPerState([
          'Needs Scheduling',
          'Awaiting Consult',
          'Consulting'
        ]),
        children: [
          {
            label: t('custom.uhb.consult.tab-unscheduled'),
            name: 'unscheduled',
            statuses: ['Needs Scheduling']
          },
          {
            label: t('custom.uhb.consult.tab-scheduled'),
            name: 'scheduled',
            statuses: ['Awaiting Consult']
          },
          {
            label: t('custom.uhb.consult.tab-consulting'),
            name: 'consulting',
            statuses: ['Consulting']
          }
        ]
      },
      {
        label: t('custom.uhb.consult.tab-group-review'),
        name: 'review',
        count: getCountPerState(['Awaiting Review', 'Reviewing']),
        children: [
          {
            label: t('custom.uhb.consult.tab-reviewing'),
            name: 'reviewing',
            statuses: ['Awaiting Review', 'Reviewing']
          }
        ]
      },
      {
        label: t('custom.uhb.consult.tab-group-admin'),
        name: 'admin',
        count: getCountPerState(['Rejected', 'In Administration']),
        children: [
          {
            label: t('custom.uhb.consult.tab-rejected'),
            name: 'rejected',
            statuses: ['Rejected']
          },
          {
            label: t('custom.uhb.consult.tab-unactioned'),
            name: 'unactioned',
            statuses: ['In Administration'] // TODO: Update status when PRO-92 is done
          },
          {
            label: t('custom.uhb.consult.tab-completed'),
            name: 'complete',
            statuses: ['Resolved Rejected', 'Complete']
          }
        ]
      }
    ] as Array<TabConsultWithChildren>
);

const activeTabIndex = ref<number>(0);
const activeChildIndex = ref<number | null>(0);

const activeChildren = computed(
  () => tabs.value[activeTabIndex.value].children as ChildTab[] | undefined
);

const hasActiveChildren = computed(
  () => activeChildren.value ? activeChildren.value.length > 0 : false
);

const select = (tabIndex: number, childIndex: number | null = null) => {
  if (props.disabled) {
    return;
  }
  activeTabIndex.value = tabIndex;
  activeChildIndex.value = childIndex;
  const status: Array<Status> = activeChildren.value
    ? activeChildren.value[activeChildIndex.value].statuses
    : [];
  emit('update:modelValue', status);

  return;
};

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(tabs), (tab, tabIndex) => {
        return (_openBlock(), _createElementBlock("div", {
          key: tabIndex,
          class: _normalizeClass(["flex select-none leading-none text-base font-medium", _unref(parentOptionStyle)]),
          "data-dusk": `consult-group-status-selector-${tab.name}`,
          onClick: ($event: any) => (select(tabIndex, tab.children ? 0 : null))
        }, [
          _createElementVNode("div", {
            class: _normalizeClass(["flex items-center space-x-2 px-4 py-3", [
            tabIndex === 0 &&
              activeTabIndex.value !== tabIndex &&
              'border-r border-gray-300',
            activeTabIndex.value === tabIndex
              ? 'bg-gray-100 rounded-md border-r border-transparent'
              : 'bg-transparent',
            activeTabIndex.value !== 0 && tabIndex === 1 && 'ml-2',
          ]])
          }, [
            _createElementVNode("div", {
              class: _normalizeClass(["leading-none text-lg font-medium", 
              activeTabIndex.value === tabIndex
                ? _unref(selectedStyle).label
                : _unref(unselectedStyle).label
            ])
            }, _toDisplayString(tab.label), 3),
            _createElementVNode("div", {
              class: _normalizeClass(["flex items-center justify-center p-1 text-2xs font-semibold", 
              activeTabIndex.value === tabIndex
                ? _unref(selectedStyle).total
                : _unref(unselectedStyle).total
            ])
            }, _toDisplayString(tab.count), 3)
          ], 2)
        ], 10, _hoisted_3))
      }), 128))
    ]),
    (_unref(activeChildren))
      ? (_openBlock(), _createElementBlock("div", _hoisted_4, [
          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(activeChildren), (child, childIndex) => {
            return (_openBlock(), _createElementBlock("div", {
              key: childIndex,
              class: _normalizeClass(["select-none leading-none text-lg font-medium pt-4", _unref(optionStyle)]),
              "data-dusk": `consult-status-selector-${child.name}`,
              onClick: ($event: any) => (select(activeTabIndex.value, childIndex))
            }, [
              _createElementVNode("div", {
                class: _normalizeClass(["flex items-center space-x-2 px-3 py-2", [
            activeChildIndex.value === childIndex
              ? 'bg-gray-100 rounded-md'
              : 'bg-transparent',
          ]])
              }, [
                _createElementVNode("div", {
                  class: _normalizeClass(["leading-none text-sm font-medium", 
              activeChildIndex.value === childIndex
                ? _unref(selectedStyle).label
                : _unref(unselectedStyle).label
            ])
                }, _toDisplayString(child.label), 3)
              ], 2)
            ], 10, _hoisted_5))
          }), 128))
        ]))
      : _createCommentVNode("", true)
  ]))
}
}

})